import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Copy, Check } from 'lucide-react';
import type { SecretMetadata } from '@/api/useSecretsApi';

interface Props {
    secret: SecretMetadata;
    onEdit: (secret: SecretMetadata) => void;
    onDelete: (secret: SecretMetadata) => void;
}

export const SecretRow: React.FC<Props> = ({ secret, onEdit, onDelete }) => {
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [copied, setCopied] = useState(false);

    const copyName = async () => {
        await navigator.clipboard.writeText(secret.name);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    return (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-neutral-100/50 dark:hover:bg-neutral-800/50 group">
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                    <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate font-mono">{secret.name}</span>
                    <button
                        onClick={copyName}
                        title="Copy secret name"
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-neutral-200/70 dark:hover:bg-neutral-700/70 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 shrink-0"
                    >
                        {copied ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
                    </button>
                    {secret.service && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 shrink-0">
                            {secret.service}
                        </span>
                    )}
                </div>
                {secret.description && (
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate mt-0.5">{secret.description}</p>
                )}
                <span className="text-[10px] text-neutral-400 dark:text-neutral-500">••••••••</span>
            </div>
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                {confirmDelete ? (
                    <>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 text-xs text-red-600 hover:text-red-700"
                            onClick={() => {
                                onDelete(secret);
                                setConfirmDelete(false);
                            }}
                        >
                            Confirm
                        </Button>
                        <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setConfirmDelete(false)}>
                            Cancel
                        </Button>
                    </>
                ) : (
                    <>
                        <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => onEdit(secret)}>
                            Edit
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 text-xs text-red-500 hover:text-red-600"
                            onClick={() => setConfirmDelete(true)}
                        >
                            Delete
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
};
